import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, Loader } from '../icons';
import { verifyEmail, resendVerification } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

function VerifyEmail() {
  const { token } = useParams();
  const { t } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isVendor, setIsVendor] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [resendOk, setResendOk] = useState(false);
  // The token is single-use: a second request (StrictMode re-runs effects in
  // development) would turn a successful verification into "failed".
  const requested = useRef(null);

  useEffect(() => {
    if (token && requested.current !== token) {
      requested.current = token;
      verifyEmail(token)
        .then((res) => {
          setStatus('success');
          setMessage(res.data.message);
          setEmail(res.data.email || '');
          if (res.data.isVendor) setIsVendor(true);
        })
        .catch((err) => {
          const data = err.response?.data;
          if (data?.expired) {
            setStatus('expired');
            setEmail(data.email || '');
            setMessage(t('verifyEmail.expiredText'));
          } else {
            setStatus('error');
            // 400 = unknown or already-used token; anything else is a server/network problem
            setMessage(err.response?.status === 400 ? t('verifyEmail.invalidText') : t('verifyEmail.genericError'));
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResend = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;
    setResending(true);
    setResendMsg('');
    try {
      const res = await resendVerification(email);
      setResendOk(true);
      setResendMsg(res.data.message);
    } catch (err) {
      // Includes "Email is already verified", which tells them to just sign in
      setResendOk(false);
      setResendMsg(err.response?.data?.error || t('verifyEmail.genericError'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <Loader size={48} className="auth-icon spin" />
              <h1 style={{ marginTop: '16px' }}>{t('verifyEmail.verifying')}</h1>
              <p style={{ color: '#666' }}>{t('verifyEmail.pleaseWait')}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={56} color="#22C55E" style={{ marginBottom: '16px' }} />
              <h1>{t('verifyEmail.success')}</h1>
              <p style={{ color: '#666', marginBottom: '16px' }}>{message}</p>
              {isVendor ? (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
                  <p style={{ color: '#92400e', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                    <strong>{t('verifyEmail.whatNext')}</strong><br />
                    {t('verifyEmail.vendorNote')}
                  </p>
                </div>
              ) : null}
              <Link to="/login" className="btn btn-primary btn-lg">
                {t('verifyEmail.signIn')}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle size={56} color="#EF4444" style={{ marginBottom: '16px' }} />
              <h1>{t('verifyEmail.failed')}</h1>
              <p style={{ color: '#666', marginBottom: '24px' }}>{message}</p>
              <form onSubmit={handleResend} className="auth-form" style={{ textAlign: 'left', marginBottom: '16px' }}>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={resending}>
                  {resending ? t('verifyEmail.sending') : t('verifyEmail.sendNewLink')}
                </button>
              </form>
              {resendMsg && (
                <p style={{ fontSize: '14px', color: resendOk ? '#22C55E' : '#EF4444', marginBottom: '16px' }}>
                  {resendMsg}
                </p>
              )}
              <Link to="/login" className="btn btn-outline">
                {t('verifyEmail.goToLogin')}
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <Mail size={56} color="#F59E0B" style={{ marginBottom: '16px' }} />
              <h1>{t('verifyEmail.expired')}</h1>
              <p style={{ color: '#666', marginBottom: '16px' }}>{message}</p>
              {email && (
                <div>
                  <button
                    className="btn btn-primary"
                    onClick={handleResend}
                    disabled={resending}
                    style={{ marginBottom: '12px' }}
                  >
                    {resending ? t('verifyEmail.sending') : t('verifyEmail.resend')}
                  </button>
                  {resendMsg && (
                    <p style={{ fontSize: '14px', color: resendOk ? '#22C55E' : '#EF4444', marginTop: '8px' }}>
                      {resendMsg}
                    </p>
                  )}
                </div>
              )}
              <div style={{ marginTop: '16px' }}>
                <Link to="/login" className="btn btn-outline">
                  {t('verifyEmail.goToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
